import React, { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';
import { MessageModal } from './MessageModal';
interface Setup2FAModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function Setup2FAModal({
  isOpen,
  onClose
}: Setup2FAModalProps) {
  const [step, setStep] = useState<'qr' | 'verify'>('qr');
  const [verificationCode, setVerificationCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'info'
  });
  // Simulated secret key
  const secretKey = 'JBSWY3DPEHPK3PXP';
  const backupCodes = ['1234-5678', '8765-4321', '2468-1357', '9753-8642', '1357-2468'];
  if (!isOpen) return null;
  const handleCopySecret = () => {
    navigator.clipboard.writeText(secretKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleVerify = () => {
    if (verificationCode.length !== 6) {
      setModalContent({
        title: 'Invalid Code',
        message: 'Please enter a valid 6-digit code from your authenticator app.',
        type: 'error'
      });
      setIsMessageModalOpen(true);
      return;
    }
    setModalContent({
      title: '2FA Enabled',
      message: 'Two-factor authentication has been successfully enabled for your account. Save your backup codes in a secure location.',
      type: 'success'
    });
    setIsMessageModalOpen(true);
    setTimeout(() => {
      onClose();
      setStep('qr');
      setVerificationCode('');
    }, 2000);
  };
  return <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-786">
        <div className="bg-white border border-gray-300 max-w-md w-full shadow-lg" data-id="element-787">
          <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-788">
            <h3 className="font-bold text-lg" data-id="element-789">
              Setup Two-Factor Authentication
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-790">
              <X className="w-5 h-5" data-id="element-791" />
            </button>
          </div>

          <div className="p-6" data-id="element-792">
            {step === 'qr' && <div className="space-y-4" data-id="element-793">
                <div className="text-sm text-gray-600" data-id="element-794">
                  <p className="mb-2" data-id="element-795">
                    <strong data-id="element-796">Step 1:</strong> Install an authenticator app on
                    your phone:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2" data-id="element-797">
                    <li data-id="element-798">Google Authenticator</li>
                    <li data-id="element-799">Microsoft Authenticator</li>
                    <li data-id="element-800">Authy</li>
                  </ul>
                </div>

                <div data-id="element-801">
                  <p className="text-sm text-gray-600 mb-2" data-id="element-802">
                    <strong data-id="element-803">Step 2:</strong> Scan this QR code with your
                    authenticator app:
                  </p>
                  <div className="flex justify-center p-4 bg-gray-50 border border-gray-200" data-id="element-804">
                    <div className="w-48 h-48 bg-white border-2 border-dashed border-gray-300 flex items-center justify-center" data-id="element-805">
                      <span className="text-xs text-gray-400 font-mono" data-id="element-806">
                        [QR Code Placeholder]
                      </span>
                    </div>
                  </div>
                </div>

                <div data-id="element-807">
                  <p className="text-sm text-gray-600 mb-2" data-id="element-808">
                    Or enter this code manually:
                  </p>
                  <div className="flex items-center gap-2" data-id="element-809">
                    <code className="flex-1 p-2 bg-gray-50 border border-gray-200 font-mono text-sm" data-id="element-810">
                      {secretKey}
                    </code>
                    <button onClick={handleCopySecret} className="p-2 border border-gray-300 hover:bg-gray-50" data-id="element-811">
                      {copied ? <Check className="w-4 h-4 text-green-600" data-id="element-812" /> : <Copy className="w-4 h-4" data-id="element-813" />}
                    </button>
                  </div>
                </div>

                <button onClick={() => setStep('verify')} className="w-full py-2 bg-[#1f2933] text-white text-sm font-bold hover:bg-gray-800" data-id="element-814">
                  Continue to Verification
                </button>
              </div>}

            {step === 'verify' && <div className="space-y-4" data-id="element-815">
                <div data-id="element-816">
                  <p className="text-sm text-gray-600 mb-2" data-id="element-817">
                    <strong data-id="element-818">Step 3:</strong> Enter the 6-digit code from your
                    authenticator app:
                  </p>
                  <input type="text" maxLength={6} value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" className="w-full p-2 border border-gray-300 bg-gray-50 text-center text-2xl font-mono tracking-widest focus:outline-none focus:border-gray-500" data-id="element-819" />
                </div>

                <div className="p-4 bg-yellow-50 border border-yellow-200" data-id="element-820">
                  <p className="text-sm font-bold text-yellow-900 mb-2" data-id="element-821">
                    Save Your Backup Codes
                  </p>
                  <p className="text-xs text-yellow-800 mb-3" data-id="element-822">
                    Store these codes in a secure location. You can use them to
                    access your account if you lose your device.
                  </p>
                  <div className="grid grid-cols-2 gap-2" data-id="element-823">
                    {backupCodes.map((code, i) => <code key={i} className="p-2 bg-white border border-yellow-300 text-xs font-mono text-center" data-id="element-824">
                        {code}
                      </code>)}
                  </div>
                </div>

                <div className="flex gap-3" data-id="element-825">
                  <button onClick={() => setStep('qr')} className="flex-1 py-2 border border-gray-300 text-sm font-bold hover:bg-gray-50" data-id="element-826">
                    Back
                  </button>
                  <button onClick={handleVerify} className="flex-1 py-2 bg-[#1f2933] text-white text-sm font-bold hover:bg-gray-800" data-id="element-827">
                    Enable 2FA
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title={modalContent.title} message={modalContent.message} type={modalContent.type} data-id="element-828" />
    </>;
}